import { Bookmark, Gauge, Info, List, Pause, Play } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'

import type { Highlight } from '@/api/services/highlight'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { RangeSlider } from '@/components/ui/slider'
import { type Chapter, formatTime, getCurrentChapterIndex } from '@/lib/cue-parser'

import { BookmarkSheet } from './bookmark-sheet'
import { ChapterSheet } from './chapter-sheet'
import { PlaybackSpeedSheet } from './playback-speed-sheet'
import { SkipIcon } from './skip-icon'

type Props = {
  title: string
  narrator?: string
  thumbnailUrl?: string
  playing: boolean
  currentTime: number
  duration: number
  playbackRate: number
  chapters: Chapter[]
  bookmarks: Highlight[]
  onPlayPause: () => void
  onSeek: (seconds: number) => void
  onSkip: (delta: number) => void
  onPlaybackRateChange: (rate: number) => void
  onAddBookmark: (note: string) => void
  onUpdateBookmarkNote: (bookmarkId: number, note: string) => void
  onDeleteBookmark: (bookmarkId: number) => void
  onInfoClick: () => void
}

export const AudioControls = ({
  title,
  narrator,
  thumbnailUrl,
  playing,
  currentTime,
  duration,
  playbackRate,
  chapters,
  bookmarks,
  onPlayPause,
  onSeek,
  onSkip,
  onPlaybackRateChange,
  onAddBookmark,
  onUpdateBookmarkNote,
  onDeleteBookmark,
  onInfoClick,
}: Props) => {
  const [isChapterSheetOpen, setIsChapterSheetOpen] = useState(false)
  const [isSpeedSheetOpen, setIsSpeedSheetOpen] = useState(false)
  const [isBookmarkSheetOpen, setIsBookmarkSheetOpen] = useState(false)
  const [isMemoDialogOpen, setIsMemoDialogOpen] = useState(false)
  const [memoText, setMemoText] = useState('')
  const wasPlayingRef = useRef(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const currentChapterIndex = getCurrentChapterIndex(chapters, currentTime)
  const currentChapter = chapters[currentChapterIndex]

  const handleSliderChange = useCallback(
    ([value]: number[]) => {
      onSeek(value)
    },
    [onSeek],
  )

  const handleChapterSelect = useCallback(
    (chapter: Chapter) => {
      onSeek(chapter.startTime)
      setIsChapterSheetOpen(false)
    },
    [onSeek],
  )

  const closeMemoDialog = useCallback(() => {
    setIsMemoDialogOpen(false)
    if (wasPlayingRef.current) onPlayPause()
  }, [onPlayPause])

  return (
    <div className="flex flex-col h-full px-6 py-4">
      {/* カバー画像 */}
      <div className="flex-1 flex items-center justify-center min-h-0">
        <div className="w-full max-w-70 aspect-square rounded-lg overflow-hidden bg-muted shadow-lg">
          {thumbnailUrl ? (
            <img src={thumbnailUrl} alt={title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-4xl text-muted-foreground">🎧</div>
          )}
        </div>
      </div>

      {/* タイトル・ナレーター */}
      <div className="mt-4 text-center space-y-1">
        <h2 className="text-base font-semibold line-clamp-2">{title}</h2>
        {narrator && <p className="text-sm text-muted-foreground">{narrator}</p>}
        {currentChapter && <p className="text-xs text-muted-foreground">{currentChapter.title}</p>}
      </div>

      {/* シークバー */}
      <div className="mt-4 space-y-1">
        <RangeSlider
          value={[currentTime]}
          onValueChange={handleSliderChange}
          min={0}
          max={duration || 1}
          step={1}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{formatTime(currentTime)}</span>
          <span>-{formatTime(Math.max(0, duration - currentTime))}</span>
        </div>
      </div>

      {/* 再生コントロール */}
      <div className="flex items-center justify-center gap-6 mt-4">
        <button
          type="button"
          onClick={() => onSkip(-30)}
          className="p-2 rounded-full active:bg-muted transition-colors"
          aria-label="30秒戻る"
        >
          <SkipIcon direction="backward" seconds={30} size={36} />
        </button>

        <button
          type="button"
          onClick={onPlayPause}
          className="p-4 rounded-full bg-primary text-primary-foreground active:opacity-80 transition-opacity"
          aria-label={playing ? '一時停止' : '再生'}
        >
          {playing ? <Pause size={32} fill="currentColor" /> : <Play size={32} fill="currentColor" />}
        </button>

        <button
          type="button"
          onClick={() => onSkip(30)}
          className="p-2 rounded-full active:bg-muted transition-colors"
          aria-label="30秒進む"
        >
          <SkipIcon direction="forward" seconds={30} size={36} />
        </button>
      </div>

      {/* ツールバー */}
      <div className="flex items-center justify-around mt-4 pb-2">
        <button
          type="button"
          onClick={() => setIsSpeedSheetOpen(true)}
          className="flex flex-col items-center gap-1 p-2"
          aria-label="再生速度"
        >
          <Gauge size={20} className="text-muted-foreground" />
          <span className="text-[10px] text-muted-foreground">{playbackRate.toFixed(2)}x</span>
        </button>

        {chapters.length > 0 && (
          <button
            type="button"
            onClick={() => setIsChapterSheetOpen(true)}
            className="flex flex-col items-center gap-1 p-2"
            aria-label="チャプター"
          >
            <List size={20} className="text-muted-foreground" />
            <span className="text-[10px] text-muted-foreground">チャプター</span>
          </button>
        )}

        <div className="flex flex-col items-center gap-1 p-2">
          <button
            type="button"
            onClick={() => {
              wasPlayingRef.current = playing
              if (playing) onPlayPause()
              setMemoText('')
              setIsMemoDialogOpen(true)
            }}
            onContextMenu={(e) => {
              e.preventDefault()
              if (bookmarks.length > 0) setIsBookmarkSheetOpen(true)
            }}
            aria-label="ブックマーク追加"
          >
            <Bookmark size={20} className="text-muted-foreground" />
          </button>
          {bookmarks.length > 0 ? (
            <button
              type="button"
              onClick={() => setIsBookmarkSheetOpen(true)}
              className="text-[10px] text-primary underline"
            >
              しおり({bookmarks.length})
            </button>
          ) : (
            <span className="text-[10px] text-muted-foreground">しおり</span>
          )}
        </div>

        <button type="button" onClick={onInfoClick} className="flex flex-col items-center gap-1 p-2" aria-label="情報">
          <Info size={20} className="text-muted-foreground" />
          <span className="text-[10px] text-muted-foreground">情報</span>
        </button>
      </div>

      {/* チャプターシート */}
      <ChapterSheet
        isOpen={isChapterSheetOpen}
        onClose={() => setIsChapterSheetOpen(false)}
        chapters={chapters}
        currentChapterIndex={currentChapterIndex}
        onChapterSelect={handleChapterSelect}
      />

      {/* 速度シート */}
      <PlaybackSpeedSheet
        isOpen={isSpeedSheetOpen}
        onClose={() => setIsSpeedSheetOpen(false)}
        playbackRate={playbackRate}
        onPlaybackRateChange={onPlaybackRateChange}
      />

      {/* メモダイアログ */}
      <Dialog
        open={isMemoDialogOpen}
        onOpenChange={(open) => {
          if (!open) closeMemoDialog()
        }}
      >
        <DialogContent
          onOpenAutoFocus={(e) => {
            e.preventDefault()
            setTimeout(() => textareaRef.current?.focus(), 0)
          }}
        >
          <DialogHeader>
            <DialogTitle>しおりを追加</DialogTitle>
          </DialogHeader>
          <textarea
            ref={textareaRef}
            value={memoText}
            onChange={(e) => setMemoText(e.target.value)}
            placeholder="メモ（任意）"
            rows={3}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={closeMemoDialog}>
              キャンセル
            </Button>
            <Button
              onClick={() => {
                onAddBookmark(memoText.trim())
                closeMemoDialog()
              }}
            >
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ブックマークシート */}
      <BookmarkSheet
        isOpen={isBookmarkSheetOpen}
        onClose={() => setIsBookmarkSheetOpen(false)}
        bookmarks={bookmarks}
        onSeek={onSeek}
        onUpdateNote={onUpdateBookmarkNote}
        onDelete={onDeleteBookmark}
      />
    </div>
  )
}
